import { computed, reactive, ref, toValue } from 'vue';
import type { SwiperInstanceLike, UseSwiperControllerOptions } from './types';

/**
 * Syncs an external active-slide index with a Swiper instance, without
 * relying on Swiper's own Pagination module. Pair with `EliCarouselDots`:
 * bind `v-model` to `activeIndex` and spread `bindings` onto `<Swiper>`.
 *
 * In loop mode Swiper clones slides, so the real slide index is read via
 * `realIndex`/written via `slideToLoop` instead of `activeIndex`/`slideTo`.
 *
 * Swiper mutates its own instance outside Vue's reactivity, so the index and
 * the `isFirst`/`isLast` edges are snapshotted from it on Swiper's events
 * (incl. `update`/`resize`, i.e. slides added or the container resized)
 * instead of being read lazily inside a computed.
 *
 * The returned object is `reactive` so consumers can keep it as a single
 * `swiperController` and access `swiperController.activeIndex` (incl. via
 * `v-model`) directly, without destructuring/aliasing each field.
 */
function useSwiperController(options: UseSwiperControllerOptions = {}) {
    const { loop = false } = options;

    const inst = ref<SwiperInstanceLike | null>(null);
    const currentIndex = ref(0);
    const isFirst = ref(true);
    const isLast = ref(false);

    const activeIndex = computed<number>({
        get: () => currentIndex.value,
        set: (index) => {
            if (toValue(loop)) {
                inst.value?.slideToLoop(index);
            } else {
                inst.value?.slideTo(index);
            }
        },
    });

    const syncState = (instance: SwiperInstanceLike): void => {
        currentIndex.value = toValue(loop) ? instance.realIndex : instance.activeIndex;
        isFirst.value = instance.isBeginning;
        isLast.value = instance.isEnd;
    };

    const prev = (): void => inst.value?.slidePrev();
    const next = (): void => inst.value?.slideNext();

    return reactive({
        inst,
        activeIndex,
        isFirst,
        isLast,
        prev,
        next,
        bindings: {
            onSwiper: (instance: SwiperInstanceLike) => {
                inst.value = instance;
                syncState(instance);
            },
            onSlideChange: syncState,
            onUpdate: syncState,
            onResize: syncState,
            onToEdge: syncState,
            onFromEdge: syncState,
        },
    });
}

export { useSwiperController };
